import { CommonModule } from '@angular/common';
// tslint:disable-next-line:max-line-length
import { Component, EventEmitter, Inject, Injectable, InjectionToken, Input, NgModule, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, map, publishLast, refCount, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Metadata } from '../models/share-point';
import { ContainsPipeModule } from '../pipes/contains';
import { SortPipeModule } from '../pipes/sort';
import { UniquePipeModule } from '../pipes/unique';
import { SharePointService, SpGetOptions } from '../services/share-point';

export const STATION_LIST_ROUTE = new InjectionToken<string>('STATION_LIST_ROUTE');
export const STATION_SELECT = new InjectionToken<string>('STATION_SELECT');
export const STATION_FILTER = new InjectionToken<string>('STATION_FILTER');

export interface StationSearch {
  type: 'station' | 'region';
  value: string;
}

interface Station {
  IATACODE: string;
  DIVISION: string;
  SUB_DIVISION: string;
  __metadata: Metadata;
}

export interface Regions {
  [name: string]: string[];
}

@Injectable({
  providedIn: 'root'
})
export class StationsService {
  private static readonly _LOCAL_STORAGE_KEY = 'SHAREPOINT_TECHOPS_STATION_LIST';
  private _stationCache$: Observable<string[]>;
  private _allStationCache$: Observable<Station[]>;
  private _regionCache$: Observable<Regions>;
  private _regionSearch: string;
  listName = 'Stations';
  spGetOptions: SpGetOptions = {
    params: {
      $top: '500',
    },
    headers: {
      Accept: 'application/json;odata=nometadata'
    }
  };

  constructor(
    @Optional() @Inject(STATION_LIST_ROUTE) endpointRoute: string,
    @Optional() @Inject(STATION_SELECT) $select: string,
    @Optional() @Inject(STATION_FILTER) $filter: string,
    private spService: SharePointService
  ) {
    this.spGetOptions.listRoute = endpointRoute || '';
    this.spGetOptions.params.$select = $select || 'IATACODE, DIVISION, SUB_DIVISION';
    if ($filter) {
      this.spGetOptions.params.$filter = $filter;
    }
  }

  private _getAll() {
    if (!this._allStationCache$) {
      this._allStationCache$ = this.spService.get<Station>(this.listName, this.spGetOptions).pipe(
        publishLast(),
        refCount(),
      );
    }

    return this._allStationCache$;
  }

  get(): Observable<string[]> {
    if (!this._stationCache$) {
      this._stationCache$ = this._getAll().pipe(
        map(stations => stations.map(station => station.IATACODE)),
        startWith(JSON.parse(localStorage[StationsService._LOCAL_STORAGE_KEY] || '[]')),
        tap(results => (localStorage[StationsService._LOCAL_STORAGE_KEY] = JSON.stringify(results))),
        publishLast(),
        refCount(),
      );
    }

    return this._stationCache$;
  }

  regions(searchFor = 'techops'): Observable<Regions> {
    const searchForLower = searchFor.toLowerCase();

    if (!this._regionCache$ || this._regionSearch !== searchForLower) {
      this._regionSearch = searchForLower;

      this._regionCache$ = this._getAll().pipe(
        map(stations => stations.reduce((acc, station) => {
          const division = (station.DIVISION || '').toLowerCase();
          const subDivision = (station.SUB_DIVISION || '').toLowerCase();
          if (this._regionSearch === division || this._regionSearch === subDivision || this._regionSearch === 'techops') {
            acc[station.DIVISION] = [...(acc[station.DIVISION] || []), station.IATACODE];
            if (!acc[station.SUB_DIVISION] || acc[station.SUB_DIVISION].indexOf(station.IATACODE) === -1) {
              acc[station.SUB_DIVISION] = [...(acc[station.SUB_DIVISION] || []), station.IATACODE];
            }
          }
          return acc;
        }, {})),
        publishLast(),
        refCount(),
      );
    }
    return this._regionCache$;
  }

}

@Component({
  selector: 'to-station-search',
  templateUrl: './station-search.html',
  styleUrls: ['./station-search.scss'],
})
export class StationSearchComponent implements OnInit, OnDestroy {
  static SEARCH = '';

  stations$: Observable<string[]>;
  private _destroy$ = new Subject<void>();
  private _debouncer: Subject<StationSearch> = new Subject<StationSearch>();
  private _stations: string[] = [];
  private _regions: string[] = [];

  @ViewChild(MatAutocompleteTrigger, { static: true }) autocompleteTrigger: MatAutocompleteTrigger;

  @Input() width: string;
  @Input() asIcon = false;
  @Input() showRegions = false;
  @Input() requireMatch = true;

  @Output() selected = new EventEmitter<StationSearch>();

  get search() {
    return StationSearchComponent.SEARCH;
  }

  set search(value: string) {
    StationSearchComponent.SEARCH = value;
  }

  get active() {
    return this.autocompleteTrigger.panelOpen;
  }


  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private stationService: StationsService,
  ) { }

  ngOnInit() {
    this._debouncer.pipe(
      debounceTime(100),
      takeUntil(this._destroy$)
    ).subscribe(value => this.selected.emit(value));

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => this.getParams(this.route)),
        takeUntil(this._destroy$)
      )
      .subscribe(param => this.search = param.station);
    this.stations$ = combineLatest([
      this.stationService.get(),
      of(this.showRegions).pipe(
        switchMap(showRegions => showRegions ? this.stationService.regions() : of({}))
      )
    ]).pipe(
      map(results => {
        this._stations = results[0] || [];
        this._regions = [];

        for (const key in results[1]) {
          if (this._regions.indexOf(key.toUpperCase()) === -1) {
            this._regions.push(key.toUpperCase());
          }
        }

        return this._stations.concat(this._regions);
      })
    );

  }

  private getParams(route: ActivatedRoute): Params {
    let params = route.snapshot.params;
    params = { ...route.snapshot.queryParams, ...params };
    if (route.children) {
      for (const r of route.children) {
        params = { ...this.getParams(r), ...params };
      }
    }
    return params;
  }

  ngOnDestroy() {
    if (this._destroy$) {
      this._destroy$.next();
      this._destroy$.unsubscribe();
    }
  }

  select(): void {
    StationSearchComponent.SEARCH = (StationSearchComponent.SEARCH || '').toUpperCase();
    const searchResults = {
      value: StationSearchComponent.SEARCH,
      type: undefined,
    };

    if (this._stations.indexOf(searchResults.value) > -1) {
      searchResults.type = 'station';
    } else if (this._regions.indexOf(searchResults.value) > -1) {
      searchResults.type = 'region';
    } else if (!this.requireMatch) {
      searchResults.type = 'station';
    } else {
      return;
    }

    this._debouncer.next(searchResults);
  }

  track(_: number, item: string) {
    return item;
  }
}

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    SortPipeModule,
    ContainsPipeModule,
    UniquePipeModule,
    RouterModule,
  ],
  declarations: [StationSearchComponent],
  exports: [
    CommonModule,
    StationSearchComponent,
    MatAutocompleteModule,
    SortPipeModule,
    ContainsPipeModule,
    UniquePipeModule,
    RouterModule,
  ],
})
export class StationSearchModule { }
